import type { AdminGroup } from '@/lib/api'

const NEWS_CATEGORIES = ['BIRTHDAY', 'NEW MEMBER', 'EVENT', 'GRADUATION', 'RELEASE', 'CONCERT', 'BEST SONG']

export interface SidebarData {
  slug: string
  date: string
  category: string
  is_featured: boolean
  group_slug: string | null
}

interface EditorDetailsSidebarProps {
  data: SidebarData
  onChange: (field: keyof SidebarData, value: string | boolean | null) => void
  onPublish: () => void
  onSaveDraft: () => void
  onCancel: () => void
  isLoading: boolean
  isEdit: boolean
  isDraft?: boolean
  groups?: AdminGroup[]
  isGroupManager?: boolean
}

export default function EditorDetailsSidebar({
  data, onChange, onPublish, onSaveDraft, onCancel, isLoading, isEdit, isDraft,
  groups, isGroupManager,
}: EditorDetailsSidebarProps) {
  return (
    <div className="p-4 space-y-5">
      {/* Draft status indicator */}
      {isEdit && isDraft && (
        <div className="flex items-center gap-1.5 text-xs text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded px-2.5 py-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
          Draft — not published
        </div>
      )}

      {/* Action buttons */}
      <div className="space-y-2">
        <button
          type="button"
          onClick={onPublish}
          disabled={isLoading}
          className="w-full bg-yellow-500 hover:bg-yellow-400 text-black text-sm font-semibold px-4 py-2 rounded transition-colors disabled:opacity-50"
        >
          {isLoading ? 'Saving…' : isEdit && !isDraft ? 'Save Changes' : 'Publish'}
        </button>
        <button
          type="button"
          onClick={onSaveDraft}
          disabled={isLoading}
          className="w-full text-sm text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 px-4 py-2 rounded transition-colors disabled:opacity-50"
        >
          Save as Draft
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="w-full text-xs text-zinc-500 hover:text-zinc-300 px-4 py-1.5 transition-colors"
        >
          Cancel
        </button>
      </div>

      <div className="border-t border-zinc-800" />

      {/* Slug */}
      <div>
        <label className="block text-xs text-zinc-400 mb-1">Slug</label>
        <input
          type="text"
          value={data.slug}
          onChange={(e) => onChange('slug', e.target.value)}
          placeholder="url-friendly-slug"
          className="w-full bg-zinc-800 text-white rounded px-3 py-2 text-xs border border-zinc-700 focus:outline-none focus:border-zinc-500"
        />
      </div>

      {/* Date */}
      <div>
        <label className="block text-xs text-zinc-400 mb-1">Date</label>
        <input
          type="date"
          value={data.date}
          onChange={(e) => onChange('date', e.target.value)}
          className="w-full bg-zinc-800 text-white rounded px-3 py-2 text-xs border border-zinc-700 focus:outline-none focus:border-zinc-500"
        />
      </div>

      {/* Category */}
      <div>
        <label className="block text-xs text-zinc-400 mb-1">Category</label>
        <select
          value={data.category}
          onChange={(e) => onChange('category', e.target.value)}
          className="w-full bg-zinc-800 text-white rounded px-3 py-2 text-xs border border-zinc-700 focus:outline-none focus:border-zinc-500"
        >
          {NEWS_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>

      {/* Featured */}
      <div className="flex items-center gap-3">
        <span className="text-xs text-zinc-400">Featured</span>
        <button
          type="button"
          onClick={() => onChange('is_featured', !data.is_featured)}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
            data.is_featured ? 'bg-yellow-500' : 'bg-zinc-600'
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
              data.is_featured ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
        <span className={`text-xs font-medium ${data.is_featured ? 'text-yellow-400' : 'text-zinc-500'}`}>
          {data.is_featured ? 'Featured' : 'Normal'}
        </span>
      </div>

      {/* Group (admin only) */}
      {!isGroupManager && groups && groups.length > 0 && (
        <div>
          <label className="block text-xs text-zinc-400 mb-1">Group (optional)</label>
          <select
            value={data.group_slug ?? ''}
            onChange={(e) => onChange('group_slug', e.target.value || null)}
            className="w-full bg-zinc-800 text-white rounded px-3 py-2 text-xs border border-zinc-700 focus:outline-none focus:border-zinc-500"
          >
            <option value="">Platform-wide</option>
            {groups.map((g) => (
              <option key={g.slug} value={g.slug}>{g.name}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  )
}
